import React, { ChangeEvent, useEffect, useState } from 'react';

import CartItemClass from 'src/state/cart-item';

import useCart from 'src/hooks/use-cart';

import { Quantity as MaxQuantitySelect } from 'src/components/quantity';
import { QuantitySelect } from 'src/components/quantity-select';
import { cartItemId } from 'src/utils/helpers/product/product';
import { getQuantityRemaining, getRangeForQuantityDropdown } from 'shared/helpers/products';
import useErnie from 'shared/hooks/use-ernie';
import useCustomerState from 'src/hooks/use-customer-state';
import useUI from 'src/hooks/use-ui';

type QuantityProps = {
  item: CartItemClass;
  setQuantityError: (value: string) => void;
  quantityPrefix?: string | undefined;
};

export const Quantity = ({ item, setQuantityError, quantityPrefix }: QuantityProps): JSX.Element => {
  const { isKiosk } = useUI();
  const { changeItemQuantity, dispensary } = useCart();
  const { product, option, quantity } = item;
  const weight = option || 'N/A';
  const showErnie = useErnie();
  const customerState = useCustomerState();
  const [hasQuantityError, setHasQuantityError] = useState(false);

  const isQuantityLimitMaxAvailableEnabled = dispensary?.storeSettings?.quantityLimitMaxAvailable ?? false;

  const quantitiesAvailableForSelection = getRangeForQuantityDropdown({
    product,
    option,
    quantityInCart: quantity,
    isKiosk,
  });
  const totalQuantityAvailable = getQuantityRemaining(product, weight, { isKiosk });

  useEffect(() => {
    setQuantityError(hasQuantityError ? `${totalQuantityAvailable} max quantity available` : '');
  }, [hasQuantityError, setQuantityError, totalQuantityAvailable]);

  const handleQuantityChange = (selectedItem: CartItemClass) => (event: ChangeEvent<HTMLSelectElement>) => {
    const id = cartItemId(selectedItem);
    const value = Number(event.target.value);
    changeItemQuantity(id, value, showErnie, customerState);
  };

  if (isQuantityLimitMaxAvailableEnabled) {
    return (
      <MaxQuantitySelect
        onQuantityChange={handleQuantityChange(item)}
        quantity={quantity}
        totalQuantityAvailable={totalQuantityAvailable}
        setHasQuantityError={setHasQuantityError}
        prefix={quantityPrefix ?? 'Qty'}
      />
    );
  }

  return (
    <QuantitySelect
      onQuantityChange={handleQuantityChange(item)}
      quantity={quantity}
      prefix={quantityPrefix ?? 'Qty'}
      quantitiesAvailableForSelection={quantitiesAvailableForSelection}
    />
  );
};
